import { useState } from "react";
import StarField from "@/components/StarField";
import TopBar from "@/components/TopBar";
import api from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";

export default function AccessCode() {
  const [code, setCode] = useState("");
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [step, setStep] = useState("code"); // "code" | "preview"
  const [preview, setPreview] = useState(null);

  // STEP 1 — verify the code + fetch event details (no charge)
  const verifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.get(`/access-codes/preview/${code.trim().toUpperCase()}`);
      setPreview(data.event);
      // Pre-fill email if code was assigned to a specific address
      if (data.assigned_email && !email) setEmail(data.assigned_email);
      setStep("preview");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid code");
    } finally {
      setLoading(false);
    }
  };

  // STEP 2 — after they've read the description, take payment info + go to Stripe
  const confirmAndCheckout = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/via-code", {
        code: code.trim().toUpperCase(),
        email: email.trim(),
        full_name: fullName.trim(),
        origin_url: window.location.origin,
      });
      setRedirecting(true);
      toast.success("Gate open · Redirecting to checkout");
      setTimeout(() => { window.location.href = data.url; }, 800);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not open checkout");
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen">
      <StarField count={150} />
      <TopBar />
      <main className="relative z-20 flex flex-col items-center justify-center px-6 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="glass-strong rounded-sm p-8 sm:p-12 w-full max-w-lg">
          {step === "code" ? (
            <>
              <div className="text-center">
                <div className="font-mono text-[10px] tracking-[0.4em] uppercase text-cyan-400/80 mb-2">— Instagram drop system —</div>
                <h1 className="font-display text-3xl sm:text-4xl font-black uppercase neon-cyan">Reserve Your Entry</h1>
                <p className="font-mono text-xs uppercase tracking-widest text-white/60 mt-4">
                  Enter your key. See the drop. Reserve.<br />No account required.
                </p>
              </div>

              <form onSubmit={verifyCode} className="mt-10 space-y-4">
                <input
                  type="text"
                  data-testid="access-code-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="ACCESS CODE"
                  className="code-input w-full py-4 rounded-sm text-white text-center tracking-[0.4em] font-mono uppercase"
                  maxLength={20}
                  required
                />
                <button
                  type="submit"
                  data-testid="access-code-verify"
                  disabled={loading}
                  className="gate w-full py-4 bg-white text-black font-display font-bold uppercase tracking-[0.3em] text-sm rounded-sm hover:scale-[1.02] transition-all disabled:opacity-60"
                >
                  {loading ? "VERIFYING…" : "Verify Key"}
                </button>
              </form>

              <div className="text-center font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mt-8">
                No code? Try <a href="/register?intent=membership" className="text-cyan-400 hover:underline">becoming a Disciple</a>.
              </div>
            </>
          ) : (
            <>
              {/* STEP 2 — event preview + shipping-form-style contact fields */}
              <div className="text-center">
                <div className="font-mono text-[10px] tracking-[0.4em] uppercase text-cyan-400/80 mb-2">— gate opened —</div>
                <h1 className="font-display text-2xl sm:text-3xl font-black uppercase neon-cyan" data-testid="preview-event-name">{preview.name}</h1>
                <div className="font-mono text-[11px] uppercase tracking-widest text-white/60 mt-3">
                  {preview.date} · {preview.hidden_location ? "Venue disclosed 24h prior" : preview.venue}
                </div>
              </div>

              {preview.image_url && (
                <div className="mt-6 aspect-video w-full overflow-hidden rounded-sm border border-white/10">
                  <img src={preview.image_url} alt={preview.name} className="w-full h-full object-cover" />
                </div>
              )}

              {preview.description && (
                <div className="mt-6 glass rounded-sm p-5 border-l-2 border-fuchsia-400/60" data-testid="preview-description">
                  <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-fuchsia-400/80 mb-3">— what awaits —</div>
                  <p className="font-mono text-sm text-white/80 leading-relaxed whitespace-pre-wrap">{preview.description}</p>
                </div>
              )}

              {/* Price breakdown */}
              <div className="mt-6 glass rounded-sm p-5 border border-cyan-400/20" data-testid="preview-price-panel">
                <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-400/80">Reserve online</div>
                    <div className="font-display text-2xl font-black text-white mt-1">${preview.price.toFixed(2)}</div>
                  </div>
                  {preview.door_price > 0 && (
                    <>
                      <div className="font-mono text-white/30">+</div>
                      <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fuchsia-400/80">Cash at door</div>
                        <div className="font-display text-2xl font-black text-fuchsia-400 mt-1">${preview.door_price.toFixed(2)}</div>
                      </div>
                      <div className="font-mono text-white/30">=</div>
                      <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">Total</div>
                        <div className="font-display text-2xl font-black text-white/80 mt-1">${(preview.price + preview.door_price).toFixed(2)}</div>
                      </div>
                    </>
                  )}
                </div>
                {preview.door_price > 0 && (
                  <div className="mt-3 font-mono text-[10px] uppercase tracking-widest text-rose-400 font-bold">
                    ◆ No cash · No entry
                  </div>
                )}
              </div>

              <form onSubmit={confirmAndCheckout} className="mt-6 space-y-3">
                <input
                  type="text"
                  data-testid="access-code-name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Full name (matches your ID)"
                  className="w-full px-4 py-3 bg-black/60 border border-white/15 rounded-sm font-mono text-sm focus:outline-none focus:border-cyan-400/80"
                  required
                  minLength={2}
                />
                <input
                  type="email"
                  data-testid="access-code-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email (for your entry pass)"
                  className="w-full px-4 py-3 bg-black/60 border border-white/15 rounded-sm font-mono text-sm focus:outline-none focus:border-cyan-400/80"
                  required
                />
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => { setStep("code"); setPreview(null); }}
                    disabled={loading || redirecting}
                    data-testid="access-code-back"
                    className="px-5 py-3 border border-white/20 text-white/70 font-display font-bold uppercase tracking-[0.3em] text-xs rounded-sm hover:bg-white/5 disabled:opacity-50"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    data-testid="access-code-submit"
                    disabled={loading || redirecting}
                    className={`gate flex-1 py-3 font-display font-bold uppercase tracking-[0.3em] text-sm rounded-sm transition-all disabled:opacity-60 ${redirecting ? "bg-cyan-400 text-black" : "bg-white text-black hover:scale-[1.02]"}`}
                  >
                    {redirecting ? "OPENING CHECKOUT…" : loading ? "…" : preview.door_price > 0 ? `Reserve — $${preview.price.toFixed(2)}` : "Reserve & Pay"}
                  </button>
                </div>
                <p className="text-center font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
                  Secure payment via Stripe. Two pieces of matching ID required at the door.
                </p>
              </form>
            </>
          )}
        </motion.div>
      </main>
    </div>
  );
}
